import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FLAG_COLORS = {
  ok: '#00C9A7', watch: '#fbbf24', spike: '#ef4444',
  under: '#60a5fa', no_data: '#94a3b8',
};

/**
 * Coach-roster leaderboard.
 *
 * Shows the coach's full active roster across every household they've
 * been invited to. Same engagement-only display rules as the
 * household leaderboard — we never compare on outcome.
 *
 * Highlights the athletes that uploaded a video in the last 7 days
 * with a "review" CTA that deep-links to the dashboard.
 */
export default function CoachRosterLeaderboard({ theme = 'dark' }) {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    axios.get(`${API}/coach/roster/leaderboard`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) return null;
  if (!data || !data.rows?.length) {
    return (
      <div
        className="rounded-2xl p-5 text-sm opacity-70"
        style={{
          background: theme === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.85)',
          border: '1px solid rgba(0,201,167,0.15)',
        }}
      >
        Your roster is empty. Invite parents to start coaching their athletes.
      </div>
    );
  }

  return (
    <section
      className="rounded-2xl p-5"
      style={{
        background: theme === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.95)',
        border: '1px solid rgba(0,201,167,0.15)',
      }}
    >
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest opacity-60 font-bold">
            Your roster this week
          </div>
          <h2 className="font-bold mt-0.5">{data.athlete_count} athletes</h2>
        </div>
        <span className="text-xs opacity-60">
          {data.household_count} households
        </span>
      </div>

      <ul className="divide-y" style={{ borderColor: 'rgba(150,160,180,0.15)' }}>
        {data.rows.map((r) => (
          <li key={r.athlete_id} className="py-3 flex items-center gap-3">
            <div className="flex-1 min-w-0">
              <div className="font-semibold truncate">{r.name}</div>
              <div className="text-xs opacity-60 truncate">
                {[r.age_group, r.primary_role, r.club_name].filter(Boolean).join(' · ') || '—'}
              </div>
              {r.latest_video_id && (
                <button
                  onClick={() => navigate(`/clips?focus=${r.latest_video_id}`)}
                  className="text-xs mt-1.5 px-2 py-0.5 rounded font-semibold"
                  style={{
                    background: 'rgba(0,201,167,0.18)',
                    color: '#00C9A7',
                    border: '1px solid rgba(0,201,167,0.4)',
                  }}
                >
                  📹 New video{r.latest_video_drill ? ` · ${r.latest_video_drill}` : ''}
                </button>
              )}
            </div>
            <Pill label="days" value={`${r.active_days}/7`} />
            <Pill label="streak" value={`🔥 ${r.streak_days}`} />
            <Pill
              label="load"
              value={r.load_flag === 'no_data' ? '—' : r.load_flag}
              color={FLAG_COLORS[r.load_flag]}
            />
          </li>
        ))}
      </ul>

      <p className="text-[10px] opacity-50 mt-3">
        Engagement only — no outcome comparisons. Coach decides what
        matters from there.
      </p>
    </section>
  );
}

function Pill({ label, value, color = '#94a3b8' }) {
  return (
    <div
      className="text-center px-2.5 py-1 rounded-lg shrink-0"
      style={{
        background: `${color}22`, border: `1px solid ${color}55`,
        color, minWidth: 56,
      }}
    >
      <div className="text-[9px] uppercase tracking-widest font-bold opacity-80">{label}</div>
      <div className="text-sm font-bold">{value}</div>
    </div>
  );
}
